import { usePageView } from "@kupikupi/analytics/react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { Suspense } from "react";
import { ImageSearchModals } from "../components/image-search/ImageSearchModals";

/**
 * Root layout · the Telegram WebView is full-bleed, so we don't need
 * the design's `.tg-frame` mockup chrome. Just an outlet — each route
 * provides its own header/body/tabbar/CTA via TgPage primitives.
 *
 * <ImageSearchModals/> lives here (a single mount) so any camera entry-point
 * triggers the same upload→crop image-search flow via the zustand store.
 *
 * The `<Suspense>` boundary wraps `<Outlet />` so the code-split route chunks
 * (every route except `/` is `React.lazy` — see router.ts) have a loading
 * fallback while their chunk streams in, instead of a blank screen. The
 * fallback is a minimal full-height shimmer; the route's own header/skeleton
 * takes over the instant its chunk resolves.
 */
export function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  usePageView(pathname);
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
      <ImageSearchModals />
    </>
  );
}

/** Lightweight loading state shown while a lazy route chunk is fetched. */
function RouteFallback() {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--color-bg, #fff)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          border: "3px solid var(--color-border, #e5e7eb)",
          borderTopColor: "var(--color-primary, #2D6A3E)",
          animation: "ml-spin 0.7s linear infinite",
        }}
      />
    </div>
  );
}
